import { Dialog, DialogPortal } from "@/components/ui/dialog";
import { useT } from "@/i18n/LanguageContext";

export const CONTACT_TELEGRAM = "https://t.me/ceo_prpgnda";
export const CONTACT_WHATSAPP = "https://wa.me/995568789002";

interface BriefFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TelegramIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.174.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 0 1 6.988 2.896 9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.886-9.885 9.886m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.548 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488" />
  </svg>
);

export const BriefFormDialog = ({ open, onOpenChange }: BriefFormDialogProps) => {
  const t = useT();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-black/80 backdrop-blur-[10px] font-['Space_Grotesk','Inter',system-ui] p-4">
          <div className="relative z-10 w-full max-w-[460px] overflow-hidden rounded-[18px] bg-[#0A0A0A] border border-white/12 text-white shadow-[0_28px_90px_rgba(0,0,0,0.9)] pa-modal-in max-h-[calc(100vh-32px)] overflow-y-auto">
            <div className="h-[3px] w-full bg-wine" />
            <div className="px-5 sm:px-8 md:px-10 pt-7 md:pt-9 pb-7 md:pb-8">
              <button
                onClick={() => onOpenChange(false)}
                className="absolute right-3 top-4 w-8 h-8 flex items-center justify-center text-base text-white/40 transition-colors duration-200 hover:text-wine active:scale-95"
                aria-label={t("Закрыть")}
              >
                ✕
              </button>

              <h2 className="mt-2 text-[19px] sm:text-[23px] md:text-[26px] font-semibold uppercase leading-[1.15] tracking-[0.02em] text-white">
                {t("Давайте поговорим")}<br />{t("о вашем бренде")}
              </h2>

              <p className="mt-4 mb-7 md:mb-8 text-[12px] sm:text-[13px] leading-relaxed text-white/55">
                {t("Диалог с основателем PROPAGANDA AI. Разберем ваш бренд и сделаем так, чтобы новые визуалы продавали для вас и заставляли говорить о вас.")}
              </p>

              <div className="flex flex-col gap-3">
                <a
                  href={CONTACT_TELEGRAM}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3.5 rounded-[12px] border border-white/10 bg-white/[0.04] px-4 py-3.5 md:py-4 transition-all duration-300 hover:border-wine/60 hover:bg-white/[0.07] active:scale-[0.985]"
                >
                  <span className="flex h-9 w-9 md:h-10 md:w-10 flex-shrink-0 items-center justify-center rounded-full bg-white/10 transition-colors duration-300 group-hover:bg-wine">
                    <TelegramIcon className="h-[18px] w-[18px] md:h-5 md:w-5 text-white" />
                  </span>
                  <span className="flex flex-col text-left">
                    <span className="text-[13px] md:text-[14px] font-medium tracking-wide text-white">Telegram</span>
                    <span className="text-[11px] md:text-[12px] text-white/45">@ceo_prpgnda</span>
                  </span>
                  <span className="ml-auto text-white/25 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-wine">→</span>
                </a>

                <a
                  href={CONTACT_WHATSAPP}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3.5 rounded-[12px] border border-white/10 bg-white/[0.04] px-4 py-3.5 md:py-4 transition-all duration-300 hover:border-wine/60 hover:bg-white/[0.07] active:scale-[0.985]"
                >
                  <span className="flex h-9 w-9 md:h-10 md:w-10 flex-shrink-0 items-center justify-center rounded-full bg-white/10 transition-colors duration-300 group-hover:bg-wine">
                    <WhatsAppIcon className="h-[18px] w-[18px] md:h-5 md:w-5 text-white" />
                  </span>
                  <span className="flex flex-col text-left">
                    <span className="text-[13px] md:text-[14px] font-medium tracking-wide text-white">WhatsApp</span>
                    <span className="text-[11px] md:text-[12px] text-white/45">+995 568 789 002</span>
                  </span>
                  <span className="ml-auto text-white/25 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-wine">→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </DialogPortal>
    </Dialog>
  );
};
